import { Container, Row, Col } from '@geberit/gdds';
import key from 'weak-key';

// styles
import styles from './picto.module.scss';

// types
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';

// components
import { type PictoItemProps, PictoItem } from './picto-item';
import { SectionClosingLine } from '../Common/section-closing-line';
import { Headline } from 'components/ContentElementsGdds/headline/headline';

// utils
import { useSectionId } from 'utils/hooks/use-section-id';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useXy } from 'utils/hooks/use-xy';
import { useNord } from 'utils/hooks/use-nord';
import { isEmpty } from 'utils/is-empty';
import { classNameBuilder } from 'utils/classNameBuilder';

const GRID_SIZE_PICTO_ITEM = {
  1: [4, 4, 6, 6],
  2: [4, 4, 6, 6],
  3: [4, 4, 4, 4],
  4: [4, 4, 4, 3],
};

type PictoProps = {
  items: PictoItemProps[];
  title?: string;
  subtitle?: string;
  text?: string;
  anchor?: string;
  border?: string;
  contentIndex?: number;
  titleInAnchor?: boolean;
  reducedMobileVersion?: boolean;
  childOf?: string;
};

export function Picto({
  title,
  subtitle,
  text,
  anchor,
  items,
  border,
  contentIndex,
  titleInAnchor,
  reducedMobileVersion,
  childOf,
}: Readonly<PictoProps>) {
  const sectionId = useSectionId(
    title ?? '',
    anchor ?? '',
    titleInAnchor ?? false,
    contentIndex ?? 0,
  );

  const isXy = useXy();
  const isNordOrXy = useNord() || isXy;
  const isChildOfAccordionOrTab = childOf && ['tab', 'accordion'].includes(childOf);

  if (isEmpty(items)) return null;

  return (
    <>
      <section
        className={classNameBuilder(
          styles['picto-wrapper'],
          !border && styles['hasSCL'],
          childOf === 'tab' && styles.marginIsTab,
          childOf === 'accordion' && styles.marginIsAccordion,
        )}
        id={sectionId}
      >
        <Container
          className={classNameBuilder(childOf === 'tab' && 'tab-child-container')}
          maxContentWidth={buildSize(gridSizes.gddsFullGrid)}
        >
          <Row justify="center">
            <Col size={[4, 8, 12]}>
              <Headline
                title={title}
                subtitle={subtitle}
                tag={isNordOrXy && isChildOfAccordionOrTab ? Formats.h4 : Formats.h2}
                variant={Formats.h2}
                titlePreviewId="#st_title"
                subtitlePreviewId="#st_subtitle"
                text={text}
                textClassName={styles.intro}
              />
            </Col>
          </Row>
          <Row
            className={styles['picto-list-wrapper']}
            justify="center"
            data-preview-id="#st_pictoItems"
          >
            <Col size={[4, 8, 12]}>
              <Row>
                {items.map((item, index) => (
                  <Col
                    key={key(item)}
                    size={
                      items.length < 4
                        ? GRID_SIZE_PICTO_ITEM[items.length]
                        : GRID_SIZE_PICTO_ITEM[4]
                    }
                    data-preview-id={`#${index}`}
                    className={isXy && styles['xy-picto-items']}
                  >
                    <PictoItem
                      headline={item.headline}
                      description={item.description}
                      imageAlt={item.imageAlt}
                      imageObject={item.imageObject}
                      imageObjectMobile={item.imageObjectMobile}
                      reducedMobileVersion={reducedMobileVersion}
                      childOf={childOf}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>
      {!border && isNordOrXy && <SectionClosingLine />}
    </>
  );
}
